import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Clock, MessageCircle, Pin, ShoppingCart, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { formatDateTime } from '../../utils/dateUtils';
import DeviceActivity from '../devices/DeviceActivity';

const RepairActivity = forwardRef(({ repairId, deviceId, onRefresh }, ref) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeviceActivity, setShowDeviceActivity] = useState(false);
  const deviceActivityRef = React.useRef();

  const fetchHistory = useCallback(async () => {
    if (!repairId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // For repairs, fetch repair history and comments only
      const [historyResponse, commentsResponse] = await Promise.all([
        axios.get(`/api/repairs/${repairId}/history`),
        axios.get(`/api/repairs/${repairId}/comments`)
      ]);
      
      // Transform comments to match history format
      const commentsData = Array.isArray(commentsResponse.data?.comments)
        ? commentsResponse.data.comments
        : Array.isArray(commentsResponse.data)
          ? commentsResponse.data
          : [];
      const transformedComments = commentsData.map(comment => ({
        id: `comment_${comment.id}`,
        event_type: 'comment',
        event_description: comment.comment_text,
        performed_by_name: comment.created_by_name,
        timestamp: comment.created_at,
        comment_data: comment
      }));
      
      // Combine and sort by timestamp (newest first). Filter out 'comment_added' events 
      // since we display actual comments separately.
      const historyData = Array.isArray(historyResponse.data) ? historyResponse.data : [];
      const filteredHistory = historyData.filter(event => event.event_type !== 'comment_added');
      const combinedHistory = [...filteredHistory, ...transformedComments]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setHistory(combinedHistory);
      setError('');
    } catch (err) {
      console.error('Error fetching repair activity:', err);
      setError('Failed to load repair activity');
    } finally {
      setLoading(false);
    }
  }, [repairId]);

  useEffect(() => {
    if (repairId) {
      fetchHistory();
    }
  }, [repairId, fetchHistory]);

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchHistory
  }), [fetchHistory]);

  // Get status color and icon based on event type
  const getEventDisplay = (event) => {
    if (event.event_type === 'comment') {
      const isPinned = event.comment_data?.is_pinned;
      return {
        color: isPinned ? 'bg-amber-500' : 'bg-blue-400',
        icon: isPinned
          ? <Pin className="h-3 w-3 text-white" />
          : <MessageCircle className="h-3 w-3 text-white" />
      };
    }
    
    if (event.event_type === 'repair_created') {
      return { color: 'bg-green-400', icon: null };
    }
    
    if (event.event_type === 'repair_module') {
      return { color: 'bg-yellow-500', icon: null };
    }
    
    if (event.event_type === 'exchange_module') {
      return { color: 'bg-purple-500', icon: null };
    }

    // Automatic b2b order status push (Repairs → Processing), see server/utils/repairOrderStatusPush.js
    if (event.event_type === 'b2b_order_status_pushed') {
      return { color: 'bg-green-500', icon: <ShoppingCart className="h-3 w-3 text-white" /> };
    }
    if (event.event_type === 'b2b_order_status_push_failed') {
      return { color: 'bg-red-500', icon: <AlertTriangle className="h-3 w-3 text-white" /> };
    }

    if (event.event_type === 'status_change') {
      const status = event.new_value;
      switch (status) {
        case 'waiting_for_repair':
          return { color: 'bg-blue-400', icon: null };
        case 'clarification_required':
          return { color: 'bg-orange-400', icon: null };
        case 'waiting_for_dev_input':
          return { color: 'bg-cyan-400', icon: null };
        case 'waiting_for_customer_response':
          return { color: 'bg-amber-400', icon: null };
        case 'work_in_progress':
          return { color: 'bg-yellow-400', icon: null };
        case 'repaired':
          return { color: 'bg-purple-400', icon: null };
        case 'ready_for_shipping':
          return { color: 'bg-indigo-400', icon: null };
        case 'waiting_for_payment':
          return { color: 'bg-red-400', icon: null };
        case 'completed':
          return { color: 'bg-green-400', icon: null };
        default:
          return { color: 'bg-gray-400', icon: null };
      }
    }
    
    // Default for other event types
    return { color: 'bg-gray-400', icon: null };
  };

  if (loading) {
    return (
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Repair Activity</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading activity...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Repair Activity</h3>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-6" data-testid="activity-component">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Repair Activity</h3>
      
      {history.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No activity yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Repair events will appear here as they occur.
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Vertical timeline line - centered on the dots */}
          <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-300"></div>
          
          {/* Timeline events */}
          <div className="space-y-4">
            {history.map((event, index) => {
              const eventDisplay = getEventDisplay(event);
              const isComment = event.event_type === 'comment';
              const isPinnedComment = isComment && event.comment_data?.is_pinned;
              
              return (
                <div key={event.id || index} className="relative flex items-start">
                  {/* Status dot - 24px wide (w-6), so center is at 12px */}
                  <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white ${eventDisplay.color}`}>
                    {eventDisplay.icon}
                  </div>
                  
                  {/* Event content */}
                  <div className="ml-4 min-w-0 flex-1">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium text-gray-500">
                        {formatDateTime(event.timestamp)}
                      </span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="font-medium text-gray-900">
                        {event.performed_by_name || 'Unknown user'}
                      </span>
                      {isComment ? (
                        <>
                          <span className="text-gray-700"> commented:</span>
                          {isPinnedComment && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                              Pinned
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="text-gray-700">
                            {' '}{event.event_description}
                          </span>
                        </>
                      )}
                    </div>
                    
                    {/* Comment content - displayed separately for better formatting */}
                    {isComment && (
                      <div className={`mt-2 p-3 rounded-md border-l-4 ${isPinnedComment ? 'bg-amber-50 border-amber-400' : 'bg-gray-50 border-blue-200'}`}>
                        <p className={`text-sm whitespace-pre-wrap ${isPinnedComment ? 'text-amber-900' : 'text-gray-700'}`}>
                          {event.event_description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show more link to display Device Activity */}
      {deviceId && (
        <div className="mt-6">
          {!showDeviceActivity ? (
            <div className="text-center">
              <button
                onClick={() => setShowDeviceActivity(true)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Show more
              </button>
            </div>
          ) : (
            <DeviceActivity
              ref={deviceActivityRef}
              deviceId={deviceId}
              onRefresh={onRefresh}
            />
          )}
        </div>
      )}
    </div>
  );
});

RepairActivity.displayName = 'RepairActivity';

export default RepairActivity;

